import { ConditionCombinationMode, ApplyToSiteMode } from '@sleed/bizcommerce-api';
import type { PromotionPolicyToken } from '@sleed/bizcommerce-api';
import type { PromotionFormValues } from '~/lib/schemas/promotion';
import type { PromotionAction } from '~/lib/schemas/actions';
import { formActionToToken, tokenToFormAction } from './action';

/**
 * Maps between the camelCase FORM shape and the generated `PromotionPolicyToken`
 * (PascalCase). Field correspondence is documented in src/lib/schemas/promotion.ts.
 *
 * Action mapping is delegated to ./action.ts (`formActionToToken`/`tokenToFormAction`),
 * the authoritative per-type Payload mapper reconciled against the backend CartAction
 * classes in BizWeb.NC.Base/CartActions/*.
 */

function dateToIso(d: Date | null): string | null {
	return d ? d.toISOString() : null;
}

function emptyToNull(s: string | null): string | null {
	const v = s?.trim();
	return v ? v : null;
}

function isoToDate(s: string | null | undefined): Date | null {
	if (!s) return null;
	const d = new Date(s);
	return Number.isNaN(d.getTime()) ? null : d;
}

const innerToEnum: Record<PromotionFormValues['productMatchCondition'], ConditionCombinationMode> = {
	any: ConditionCombinationMode.Or,
	all: ConditionCombinationMode.And
};

const outerToEnum: Record<PromotionFormValues['productSelectionConditionMode'], ConditionCombinationMode> = {
	or: ConditionCombinationMode.Or,
	and: ConditionCombinationMode.And
};

const siteModeToEnum: Record<PromotionFormValues['applyToSiteMode'], ApplyToSiteMode> = {
	all: ApplyToSiteMode.AllSites,
	default: ApplyToSiteMode.DefaultSite
};

// Action mapping delegates to the per-type Payload mapper in ./action.

export function formToToken(form: PromotionFormValues): PromotionPolicyToken {
	return {
		id: form.id,
		// formToToken always emits the current token format; the backend rejects a
		// missing/zero SchemaVersion with `{"schemaVersion":["Invalid version number"]}`.
		SchemaVersion: 2,
		Name: form.name,
		Code: form.code,
		Priority: form.priority,
		Description: form.description,
		BadgeText: form.badgeText,
		BadgeImageUrl: form.badgeImageUrl,
		BackgroundImageUrl: form.backgroundImageUrl,
		FrontLabel: emptyToNull(form.frontLabel),
		ColorHex: emptyToNull(form.colorHex),
		BackgroundColorHex: emptyToNull(form.backgroundColorHex),
		ExternalUrl: emptyToNull(form.externalUrl),
		IsActive: form.isActive,
		iv: form.isVisible,
		HideFromOffers: form.hideFromOffers,
		ActiveFrom: dateToIso(form.activeFrom),
		ActiveTo: dateToIso(form.activeTo),
		MinAmount: form.minAmount,
		MaxAmount: form.maxAmount,
		MinQuantity: form.minQuantity,
		AppliesToSelectedProducts: form.appliesToSelectedProducts,
		ProductMatchCondition: innerToEnum[form.productMatchCondition],
		ProductSelectionConditionMode: outerToEnum[form.productSelectionConditionMode],
		ApplyToSiteMode: siteModeToEnum[form.applyToSiteMode],
		ProductIds: form.productIds,
		CategoryIds: form.categoryIds,
		ManufacturersIds: form.manufacturerIds,
		CustomerCategoryIds: form.customerCategoryIds,
		CustomerIds: form.customerIds,
		CustomerGroupsIds: form.customerGroupIds,
		SiteIds: form.siteIds,
		CartActions: form.actions.map(formActionToToken)
	};
}

export function tokenToForm(token: PromotionPolicyToken): PromotionFormValues {
	const inner = token.ProductMatchCondition === ConditionCombinationMode.And ? 'all' : 'any';
	const outer = token.ProductSelectionConditionMode === ConditionCombinationMode.Or ? 'or' : 'and';
	const siteMode = token.ApplyToSiteMode === ApplyToSiteMode.DefaultSite ? 'default' : 'all';
	return {
		id: token.id,
		name: token.Name ?? '',
		code: token.Code ?? '',
		priority: token.Priority ?? 1,
		description: token.Description ?? null,
		badgeText: token.BadgeText ?? null,
		badgeImageUrl: token.BadgeImageUrl ?? null,
		backgroundImageUrl: token.BackgroundImageUrl ?? null,
		frontLabel: token.FrontLabel ?? null,
		colorHex: token.ColorHex ?? null,
		backgroundColorHex: token.BackgroundColorHex ?? null,
		externalUrl: token.ExternalUrl ?? null,
		isActive: token.IsActive ?? true,
		isVisible: token.iv ?? true,
		hideFromOffers: token.HideFromOffers ?? false,
		activeFrom: isoToDate(token.ActiveFrom as string | null),
		activeTo: isoToDate(token.ActiveTo as string | null),
		minAmount: token.MinAmount ?? null,
		maxAmount: token.MaxAmount ?? null,
		minQuantity: token.MinQuantity ?? null,
		appliesToSelectedProducts: token.AppliesToSelectedProducts ?? false,
		productMatchCondition: inner,
		productSelectionConditionMode: outer,
		productIds: token.ProductIds ?? [],
		categoryIds: token.CategoryIds ?? [],
		manufacturerIds: token.ManufacturersIds ?? [],
		customerCategoryIds: token.CustomerCategoryIds ?? [],
		customerIds: token.CustomerIds ?? [],
		customerGroupIds: token.CustomerGroupsIds ?? [],
		siteIds: token.SiteIds ?? [],
		applyToSiteMode: siteMode,
		actions: (token.CartActions ?? []).map(tokenToFormAction).filter((a): a is PromotionAction => a !== null)
	};
}
